import { useLayoutEffect } from 'react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { JoinCreateView } from '@/components/dashboard/join-create-view';
import { applyThemeColors } from '@/lib/brand-theme';
import { notifyNativeSafeAreaTheme } from '@/lib/native-safe-area-theme';
import { useTenant } from '@/providers/tenant-context';
import { useUser } from '@/hooks/use-user';
import { AccountHeader } from '@/components/layouts/AccountHeader';
import { canCreateCommunity, canCreateTopLevelCommunity } from '@/lib/community-permissions';

const CREATE_COMMUNITY_TABS = ['open-explore', 'known-explore', 'create'] as const;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 32, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring' as const, stiffness: 260, damping: 22 },
  },
};

interface ChoiceCardProps {
  label: string;
  onClick: () => void;
}

function ChoiceCard({ label, onClick }: ChoiceCardProps) {
  return (
    <motion.button
      variants={cardVariants}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="w-full max-w-sm mx-auto text-center rounded-xl py-5 px-6 bg-primary text-primary-foreground font-bold text-lg shadow-[4px_4px_8px_rgba(0,0,0,0.25)] transition-all duration-200 cursor-pointer border border-primary/10 hover:shadow-[6px_6px_12px_rgba(0,0,0,0.3)]"
    >
      {label}
    </motion.button>
  );
}

export default function CreateCommunityPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenant, isWhitelabel } = useTenant();
  const { user } = useUser();
  const canCreate = canCreateCommunity({
    isWhitelabel,
    allowCreateCommunity: tenant?.allowCreateCommunity,
    communities: user?.communities ?? [],
  });
  const canCreateTopLevel = canCreateTopLevelCommunity({
    isWhitelabel,
    allowCreateCommunity: tenant?.allowCreateCommunity,
  });
  const [tab, setTab] = useQueryState(
    'tab',
    parseAsStringLiteral(CREATE_COMMUNITY_TABS).withDefault('create')
  );

  useLayoutEffect(() => {
    applyThemeColors(tenant?.themeColors);
    notifyNativeSafeAreaTheme();
  }, [tenant?.themeColors]);

  const hasTabParam = searchParams.has('tab');
  const parentId = searchParams.get('parent');

  const createCommunityPath = (tab: (typeof CREATE_COMMUNITY_TABS)[number]) => {
    const params = new URLSearchParams({ tab });
    if (parentId) params.set('parent', parentId);
    return `/create-community?${params.toString()}`;
  };

  if (hasTabParam) {
    return (
      <JoinCreateView
        activeTab={tab as 'open-explore' | 'known-explore' | 'create'}
        defaultTab="create"
        onTabChange={(nextTab) => void setTab(nextTab)}
      />
    );
  }

  return (
    <div className="min-h-dvh flex flex-col bg-background relative overflow-hidden transition-colors duration-300">
      {user ? <AccountHeader user={user} /> : null}

      <div className="flex flex-1 flex-col md:items-center md:justify-center p-0 md:p-6 relative">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45 }}
          className="w-full md:max-w-2xl flex-1 md:flex-none relative z-10 flex flex-col"
        >
          <div className="glass-card rounded-none md:rounded-[32px] overflow-hidden shadow-2xl flex-1 flex flex-col bg-card">
            <div className="text-center pt-20 pb-6 px-8 relative">
              <motion.button
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                onClick={() => navigate('/dashboard')}
                className="absolute top-8 left-6 w-10 h-10 flex items-center justify-center rounded-xl border border-border/60 bg-background/60 hover:bg-accent hover:border-primary/30 text-foreground hover:text-primary transition-all duration-200 shadow-sm hover:shadow-md group"
                aria-label="Go back to dashboard"
              >
                <ArrowLeft className="h-6 w-6 stroke-[3] group-hover:scale-110 transition-transform" />
              </motion.button>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18 }}
                className="text-4xl font-extrabold tracking-tighter mb-3 bg-clip-text text-transparent"
                style={{
                  backgroundImage: `linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.7) 60%, hsl(var(--primary) / 0.4) 100%)`,
                }}
              >
                Welcome to {tenant?.name || 'Milora'}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.24 }}
                className="text-muted-foreground text-base font-medium max-w-sm mx-auto"
              >
                Join an existing open community
                {canCreate
                  ? canCreateTopLevel
                    ? ' or build a new one.'
                    : ' or create a sub-community.'
                  : '.'}
              </motion.p>
            </div>

            <div className="px-6 pb-8">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="flex flex-col gap-4"
              >
                {canCreate && (
                  <ChoiceCard
                    label={canCreateTopLevel ? 'Create a Community' : 'Create a Sub-Community'}
                    onClick={() => navigate(createCommunityPath('create'))}
                  />
                )}
                <ChoiceCard
                  label="Join a known Community"
                  onClick={() => navigate(createCommunityPath('known-explore'))}
                />
                <ChoiceCard
                  label="Join an Open Community"
                  onClick={() => navigate(createCommunityPath('open-explore'))}
                />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
